import interceptor, { baseUrl } from '../common/interceptor';
import axios, { AxiosResponse } from 'axios';
import { APIPaths } from '../common/constants';
import type { WcProjectSelectOption } from '../constants/worldCourierProjectSelectOptions';
import {
  ApiResponse,
  AdditionalRecipientOption,
  AlertAssignmentSnapshot,
  ProductDetailColumns,
  ExecutionLegDetail,
  GeoFenceStopsResponse,
  RouteDeviationCandidates,
  RouteDeviationLookup,
  RouteDeviationSetting,
  SaveGeoFencePayload,
  SaveRouteDeviationPayload,
  SaveGeoFenceResponse,
  ShipmentGeoFenceResponse,
  ShipmentAlertData,
  ShipmentLegListItem,
  ShipmentProductList,
  SingleShipmentData,
  SingleShipmentDataExtended,
  TeamWithUsers,
} from '@/apis/types';
import { ActivityItem } from '@/pages/Shipments/AiTranscripts/ui/AIAlertDetails';
import { HeaderItem } from '@/pages/Data/types';
import { FiltersPayload } from '@/pages/Data/types';
import { getUserTimeZone } from '@/common/interceptor/interceptor';
import {
  CalendarShipmentsRequestBody,
  MoreShipmentsApiResponse,
} from '@/pages/Shipments/ControlTower/types';
import { OperationType } from '@/common/enum';
import type { EmailPanelNative } from './emailReplyContract';
import type { TelemetryVital } from './telemetry';

export interface ShipmentRequestBody {
  page: number;
  page_size: number;
  search: string;
  [key: string]: unknown;
}

export const GetShipments = (
  token: string,
  params: string
): Promise<unknown> => {
  const queryString = new URLSearchParams(params).toString();
  const baseUrl = APIPaths.shipments;
  const url = queryString ? `${baseUrl}?${queryString}` : baseUrl;
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', url, token)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.error('Error fetching shipments:', error?.response);
        reject(error?.response);
      });
  });
};

export const PostShipments = (
  token: string,
  body: ShipmentRequestBody
): Promise<unknown> => {
  const url = APIPaths.post_shipments;

  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost('Post', url, body, token, null, OperationType.Read)
      .then((res) => resolve(res))
      .catch((error) => {
        console.error('Error fetching shipments:', error?.response);
        reject(error?.response);
      });
  });
};

export const PostShipmentsV3 = (
  token: string,
  body: ShipmentRequestBody
): Promise<unknown> => {
  const url = APIPaths.post_shipments_v3;

  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost('Post', url, body, token, null, OperationType.Read)
      .then((res) => resolve(res))
      .catch((error) => {
        console.error('Error fetching shipments v3:', error?.response);
        reject(error?.response);
      });
  });
};

export const PostExecutionLegs = (
  token: string,
  body: ShipmentRequestBody
): Promise<unknown> => {
  const url = APIPaths.execution_legs;

  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost('Post', url, body, token, null, OperationType.Read)
      .then((res) => resolve(res))
      .catch((error) => {
        console.error('Error fetching execution legs:', error?.response);
        reject(error?.response);
      });
  });
};

export const GetExecutionLegDetail = (
  token: string,
  elId: string
): Promise<ExecutionLegDetail> => {
  const url = APIPaths.execution_leg_detail(elId);
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', url, token)
      .then((res) => {
        const response = res as ApiResponse<ExecutionLegDetail>;
        const detail = response?.data?.data?.data;
        if (detail) {
          resolve(detail);
          return;
        }
        resolve((res as { data: ExecutionLegDetail }).data);
      })
      .catch((error) => {
        console.error('Error fetching execution leg detail:', error?.response);
        reject(error?.response);
      });
  });
};

export const PostCustomerLegExecutionLegs = (
  token: string,
  shipmentId: string
): Promise<unknown> => {
  const url = APIPaths.customer_leg_execution_legs;

  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost(
        'Post',
        url,
        { shipment_id: shipmentId },
        token,
        null,
        OperationType.Read
      )
      .then((res) => resolve(res))
      .catch((error) => {
        console.error(
          'Error fetching customer leg execution legs:',
          error?.response
        );
        reject(error?.response);
      });
  });
};

export const MoreShipmentsCalendar = async (
  token: string,
  body: CalendarShipmentsRequestBody
): Promise<MoreShipmentsApiResponse> => {
  const url = APIPaths.more_shipments_calendar;

  try {
    const res = await interceptor.axiosPost(
      'Post',
      url,
      body,
      token,
      null,
      OperationType.Read,
      true
    );
    return res as MoreShipmentsApiResponse;
  } catch (error) {
    const response = axios.isAxiosError(error) ? error.response : undefined;
    console.error('Error fetching calendar shipments:', response);
    throw response ?? error;
  }
};

export const PostShipmentsCalendar = (
  token: string,
  body: CalendarShipmentsRequestBody
): Promise<unknown> => {
  const url = APIPaths.post_shipments_calendar;

  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost('Post', url, body, token, null, OperationType.Read)
      .then((res) => resolve(res))
      .catch((error) => {
        console.error('Error fetching calendar shipments:', error?.response);
        reject(error?.response);
      });
  });
};

export interface ViewerData {
  created_by?: string;
  name?: string;
  user?: string;
  id?: string;
  role?: string;
  email?: string;
}

interface GetShipmentsViewersResponse {
  data?: {
    data?: {
      is_success?: boolean;
      message?: string;
      data?: ViewerData[];
    };
  };
}

export const GetShipmentsViewers = (
  token: string,
  shipmentId: string
): Promise<GetShipmentsViewersResponse> => {
  const url = `${APIPaths.shipment_viewer}${shipmentId}`;

  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', url, token)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.error('Error fetching shipments:', error?.response);
        reject(error?.response);
      });
  });
};
export interface OptionItem {
  id: string;
  name?: string;
  title?: string;
}
interface ShipmentStatusResponse {
  data: {
    is_success: boolean;
    data: { is_success: boolean; data: OptionItem[] };
    message?: string;
    status?: number;
  };
}

export type legTypes = 'CL' | 'EL';
export const GetShipmentsStatus = (
  token: string,
  legType?: legTypes
): Promise<ShipmentStatusResponse> => {
  const url = legType
    ? `${APIPaths.shipmentStatus}?leg_type=${legType}`
    : `${APIPaths.shipmentStatus}`;

  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', url, token)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.error('Error fetching shipments status:', error?.response);
        reject(error?.response);
      });
  });
};

export const GetShipmentsByFilter = (
  token: string,
  params: string,
  queryData = {}
): Promise<unknown> => {
  const queryString = new URLSearchParams(queryData).toString();
  const url = `${APIPaths.shipments}?${
    queryString !== '' ? queryString + '&' : ''
  }${params}`;
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', url, token, null)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.error('error?.response?.data?.error', error?.response);
        reject(error?.response);
      });
  });
};

export type SingleShipmentResponse = ApiResponse<
  // If "isHome=true", then extended data is returned; else normal data
  SingleShipmentData | SingleShipmentDataExtended
>;

/**
 * Fetch data about a single shipment
 *
 * @param token user auth token
 * @param id the shipment ID
 * @param isHome if true, only return a simple set of data that omits
 *  shipment_montor, etc
 *
 * @returns the response from the server
 */
export const GetSingleShipment = (
  token: string,
  id: string,
  isHome: boolean = false,
  includeMonitor: boolean = true,
  sensorPayloadMode: 'full' | 'full_vitals' | 'minimal' | 'none' = 'full',
  legType?: string
): Promise<SingleShipmentResponse> => {
  const monitorParam = includeMonitor ? '' : '&include_monitor=false';
  const sensorPayloadModeParam =
    isHome || sensorPayloadMode === 'full'
      ? ''
      : `&sensor_payload_mode=${sensorPayloadMode}`;
  const legTypeParam = legType ? `&leg_type=${legType}` : '';
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet(
        'Get',
        APIPaths.shipments +
          id +
          `/?home=${isHome}${monitorParam}${sensorPayloadModeParam}${legTypeParam}`,
        token,
        null
      )
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.error('error?.response?.data?.error', error?.response);
        reject(error?.response);
      });
  });
};

export const GetSingleShipmentPublically = (
  data: string,
  id: string,
  apiParams: string
): Promise<unknown> => {
  const url = `${APIPaths.shipments + id}/?token=${data}&preferred_timezone=${apiParams}`;
  return new Promise((resolve, reject) => {
    interceptor
      .axiosWithoutHaeders('Get', url, null)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.error('error?.response?.data?.error', error?.response);
        reject(error?.response);
      });
  });
};

export const GetShipmentSummary = (
  token: string,
  tabId?: string | null,
  legType: legTypes = 'CL'
): Promise<unknown> => {
  const params = new URLSearchParams();
  if (tabId) {
    params.set('tab_id', tabId);
  }
  params.set('leg_type', legType);
  const query = params.toString() ? `?${params.toString()}` : '';
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', `${APIPaths.shipments}summary/${query}`, token, null)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.error('error?.response?.data?.error', error?.response);
        reject(error?.response);
      });
  });
};

export const GetShipmentsVersion = (
  token: string,
  sinceVersion?: number
): Promise<unknown> => {
  const query = sinceVersion
    ? `?since_version=${encodeURIComponent(String(sinceVersion))}`
    : '';
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', `${APIPaths.shipments}version/${query}`, token, null)
      .then((res) => resolve(res))
      .catch((error) => reject(error?.response));
  });
};

export interface TiveShipmentCreateResult {
  public_shipment_id: string;
  devices_attached: string[];
  devices_failed: string[];
}

/** Create the Tive shipment covering this shipment's unlinked Tive sensors. */
export const CreateTiveShipment = (
  token: string,
  shipmentId: string
): Promise<{ data: TiveShipmentCreateResult }> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost(
        'Post',
        `${APIPaths.shipments}${shipmentId}/tive-shipments/`,
        {},
        token
      )
      .then((res) => resolve(res))
      .catch((error) => reject(error?.response));
  });
};

export interface TiveShipmentAccess {
  /** 'active' when the company holds an authenticated Tive connection. */
  status: string;
  /** Why not: no_connection | public_link_only | connection_inactive. */
  reason: string;
  /** The Tive sensor registry row the checkbox applies to. */
  sensor_id: string;
}

/** Whether this company may auto-create Tive shipments, and on which sensor. */
export const GetTiveShipmentAccess = (
  token: string
): Promise<{ data: TiveShipmentAccess }> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet(
        'Get',
        `${APIPaths.shipments}tive-shipments/access/`,
        token,
        null
      )
      .then((res) => resolve(res))
      .catch((error) => reject(error?.response));
  });
};

//Call For Add new Shipment [by Nisha]
export const CreateNewShipment = (
  token: string,
  model: unknown
): Promise<ApiResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost('Post', APIPaths.addNewShipment, model, token)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.error('error?.response?.data?.error', error?.response);
        reject(error?.response);
      });
  });
};

export interface PromoteShipmentPayload {
  courier_id: string;
  courier_tracking_id: string;
  courier_service_type?: string;
  courier_service_level?: string;
  courier_account_number?: string;
  /** BioCair JobId — set-once on promote when blank. */
  link_code?: string;
  /** One entry per box (tracking number, size, products) for multi-box bookings. */
  pieces?: Array<Record<string, unknown>>;
}

export const LookupShipmentByReference = (
  token: string,
  reference: string
): Promise<ApiResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet(
        'Get',
        `${APIPaths.shipmentReferenceLookup}?reference=${encodeURIComponent(reference)}`,
        token
      )
      .then((res) => resolve(res))
      .catch((error) => reject(error?.response));
  });
};

export const SuggestShipmentReferenceNumber = (
  token: string
): Promise<ApiResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', APIPaths.shipmentReferenceNumberSuggest, token)
      .then((res) => resolve(res))
      .catch((error) => reject(error?.response));
  });
};

export const PromoteShipment = (
  token: string,
  shipmentId: string,
  payload: PromoteShipmentPayload
): Promise<ApiResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost(
        'Post',
        `${APIPaths.shipmentPromote}${shipmentId}/promote/`,
        payload,
        token
      )
      .then((res) => resolve(res))
      .catch((error) => reject(error?.response));
  });
};

export type ReassignShipmentCarrierPayload = {
  courier_id?: string;
  courier_tracking_id?: string;
};

export const ReassignShipmentCarrier = (
  token: string,
  shipmentId: string,
  payload: ReassignShipmentCarrierPayload
): Promise<ApiResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost(
        'Post',
        `${APIPaths.shipmentReassignCarrier}${shipmentId}/reassign-carrier/`,
        payload,
        token
      )
      .then((res) => resolve(res))
      .catch((error) => reject(error?.response));
  });
};

export const GetShipmentBookingTemplates = (
  token: string
): Promise<ApiResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', APIPaths.shipmentBookingTemplates, token)
      .then((res) => resolve(res))
      .catch((error) => {
        console.error(
          'Error fetching shipment booking templates:',
          error?.response
        );
        reject(error?.response);
      });
  });
};

/**
 * Lightweight check for the QuickSTAT "book" menu: does the company have at
 * least one usable booking template? Avoids fetching/serializing the full
 * template list (~40 fields per row) just to compute a boolean.
 */
export const GetHasShipmentBookingTemplateSelection = (
  token: string
): Promise<ApiResponse<{ has_template_selection: boolean }>> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet(
        'Get',
        `${APIPaths.shipmentBookingTemplates}?minimal=true`,
        token
      )
      .then((res) => resolve(res))
      .catch((error) => {
        console.error(
          'Error fetching shipment booking template existence check:',
          error?.response
        );
        reject(error?.response);
      });
  });
};

export interface QuickstatCompanyBookingProfile {
  skip_account_selection_ui?: boolean;
  skip_service_type_selection?: boolean;
  force_cost_center?: string;
  /** Template name substrings → cost center override (e.g. SCDCOMMAPH → PO000275). */
  force_cost_center_by_template?: Record<string, string>;
  temperature_monitor?: string | null;
  container_name?: string;
  container_qty?: number;
  contact_phone?: string;
  contact_email?: string;
  contact_name_from_submitter?: boolean;
  /** Per-user QuickSTAT phone map; null/absent when unmapped. */
  mapped_contact_phone?: string | null;
}

export const GetQuickstatCompanyBookingProfile = (
  token: string
): Promise<ApiResponse<QuickstatCompanyBookingProfile>> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', APIPaths.shipmentBookingQuickstatCompanyProfile, token)
      .then((res) => resolve(res))
      .catch((error) => reject(error?.response));
  });
};

export const GetWorldCourierProjectOptions = (
  token: string
): Promise<ApiResponse<WcProjectSelectOption[]>> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', APIPaths.worldCourierBookingProjectOptions, token)
      .then((res) => resolve(res))
      .catch((error) => {
        console.error(
          'Error fetching World Courier project options:',
          error?.response
        );
        reject(error?.response);
      });
  });
};

export const GetWorldCourierBookingTemplates = (
  token: string
): Promise<ApiResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', APIPaths.worldCourierBookingTemplates, token)
      .then((res) => resolve(res))
      .catch((error) => {
        console.error(
          'Error fetching World Courier booking templates:',
          error?.response
        );
        reject(error?.response);
      });
  });
};

export const CreateWorldCourierBookingTemplate = (
  token: string,
  model: unknown
): Promise<ApiResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost('Post', APIPaths.worldCourierBookingTemplates, model, token)
      .then((res) => resolve(res))
      .catch((error) => {
        console.error(
          'Error creating World Courier booking template:',
          error?.response
        );
        reject(error?.response);
      });
  });
};

export const DeleteWorldCourierBookingTemplate = (
  token: string,
  templateId: string
): Promise<ApiResponse> => {
  const url = `${APIPaths.worldCourierBookingTemplates}${templateId}/`;
  return new Promise((resolve, reject) => {
    interceptor
      .axiosDelete('Delete', url, undefined, token)
      .then((res) => resolve(res))
      .catch((error) => {
        console.error(
          'Error deleting World Courier booking template:',
          error?.response
        );
        reject(error?.response);
      });
  });
};

export const CreateShipmentBooking = (
  token: string,
  model: unknown
): Promise<ApiResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost('Post', APIPaths.shipmentBookings, model, token)
      .then((res) => resolve(res))
      .catch((error) => {
        console.error('Error creating shipment booking:', error?.response);
        reject(error?.response);
      });
  });
};

export const CreateWorldCourierBooking = (
  token: string,
  model: unknown
): Promise<ApiResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost('Post', APIPaths.worldCourierBookings, model, token)
      .then((res) => resolve(res))
      .catch((error) => {
        console.error('Error creating World Courier booking:', error?.response);
        reject(error?.response);
      });
  });
};

export const GetWorldCourierBookingById = (
  token: string,
  bookingId: string
): Promise<ApiResponse> => {
  const url = `${APIPaths.worldCourierBookings}${bookingId}/`;
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', url, token)
      .then((res) => resolve(res))
      .catch((error) => {
        console.error('Error fetching World Courier booking:', error?.response);
        reject(error?.response);
      });
  });
};

export const PatchWorldCourierBooking = (
  token: string,
  bookingId: string,
  payload: Record<string, unknown>
): Promise<ApiResponse> => {
  const url = `${APIPaths.worldCourierBookings}${bookingId}/`;
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost('Patch', url, payload, token)
      .then((res) => resolve(res))
      .catch((error) => {
        console.error('Error updating World Courier booking:', error?.response);
        reject(error?.response);
      });
  });
};

export const PostWorldCourierBookingAction = (
  token: string,
  bookingId: string,
  payload: { action: 'CONFIRM' | 'CANCEL'; reason?: string }
): Promise<ApiResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost(
        'Post',
        `${APIPaths.worldCourierBookings}${bookingId}/action/`,
        payload,
        token
      )
      .then((res) => resolve(res))
      .catch((error) => {
        console.error(
          'Error posting World Courier booking action:',
          error?.response
        );
        reject(error?.response);
      });
  });
};

export const CreateCryoportBooking = (
  token: string,
  model: unknown
): Promise<ApiResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost('Post', APIPaths.cryoportBookings, model, token)
      .then((res) => resolve(res))
      .catch((error) => {
        console.error('Error creating Cryoport booking:', error?.response);
        reject(error?.response);
      });
  });
};

export const GetCryoportBookingById = (
  token: string,
  bookingId: string
): Promise<ApiResponse> => {
  const url = `${APIPaths.cryoportBookings}${bookingId}/`;
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', url, token)
      .then((res) => resolve(res))
      .catch((error) => {
        console.error('Error fetching Cryoport booking:', error?.response);
        reject(error?.response);
      });
  });
};

export const PostCryoportBookingAction = (
  token: string,
  bookingId: string,
  payload: { action: 'CONFIRM' | 'CANCEL'; reason?: string }
): Promise<ApiResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost(
        'Post',
        `${APIPaths.cryoportBookings}${bookingId}/action/`,
        payload,
        token
      )
      .then((res) => resolve(res))
      .catch((error) => {
        console.error(
          'Error posting Cryoport booking action:',
          error?.response
        );
        reject(error?.response);
      });
  });
};

export const GetCryoportLocationTemplates = (
  token: string
): Promise<ApiResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', APIPaths.cryoportLocationTemplates, token)
      .then((res) => resolve(res))
      .catch((error) => {
        console.error(
          'Error fetching Cryoport location templates:',
          error?.response
        );
        reject(error?.response);
      });
  });
};

export const CreateCryoportLocationTemplate = (
  token: string,
  model: unknown
): Promise<ApiResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost('Post', APIPaths.cryoportLocationTemplates, model, token)
      .then((res) => resolve(res))
      .catch((error) => {
        console.error(
          'Error creating Cryoport location template:',
          error?.response
        );
        reject(error?.response);
      });
  });
};

export const DeleteCryoportLocationTemplate = (
  token: string,
  templateId: string
): Promise<ApiResponse> => {
  const url = `${APIPaths.cryoportLocationTemplates}${templateId}/`;
  return new Promise((resolve, reject) => {
    interceptor
      .axiosDelete('Delete', url, undefined, token)
      .then((res) => resolve(res))
      .catch((error) => {
        console.error(
          'Error deleting Cryoport location template:',
          error?.response
        );
        reject(error?.response);
      });
  });
};

export const GetShipmentBookingById = (
  token: string,
  bookingId: string
): Promise<ApiResponse> => {
  const url = `${APIPaths.shipmentBookings}${bookingId}/`;
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', url, token)
      .then((res) => resolve(res))
      .catch((error) => {
        console.error('Error fetching shipment booking:', error?.response);
        reject(error?.response);
      });
  });
};

export const PatchShipmentBooking = (
  token: string,
  bookingId: string,
  payload: Record<string, unknown>
): Promise<ApiResponse> => {
  const url = `${APIPaths.shipmentBookings}${bookingId}/`;
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost('Patch', url, payload, token)
      .then((res) => resolve(res))
      .catch((error) => {
        console.error('Error updating shipment booking:', error?.response);
        reject(error?.response);
      });
  });
};

export const PostShipmentBookingAction = (
  token: string,
  bookingId: string,
  payload: { action: 'CONFIRM' | 'CANCEL'; reason?: string }
): Promise<ApiResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost(
        'Post',
        `${APIPaths.shipmentBookings}${bookingId}/action/`,
        payload,
        token
      )
      .then((res) => resolve(res))
      .catch((error) => {
        reject(error?.response);
      });
  });
};

export const UpdateShipment = (
  token: string,
  model: unknown
): Promise<ApiResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost('Post', APIPaths.addNewShipment, model, token)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.error('error?.response?.data?.error', error?.response);
        reject(error?.response);
      });
  });
};

//Call For Add new Shipment [by Nisha]

export const GetShipmentByID = (
  token: string,
  id: string
): Promise<unknown> => {
  const url = `${APIPaths.addNewShipment}${id}/`;
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', url, token, null)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.error('error?.response?.data?.error', error?.response);
        reject(error?.response);
      });
  });
};

//Add comments for chain of custody
export const ChainOfCustodyComments = (
  token: string,
  model: FormData
): Promise<ApiResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPostForUpload('Post', APIPaths.custody_comments, model, token)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.error(
          'error?.response white replay a comment',
          error?.response
        );
        reject(error?.response);
      });
  });
};

export interface ResolveAlertCommentsModel {
  is_resolved: boolean;
  pk_list: string[];
  comment?: string;
  /** Company resolution or dismissal tag chosen for this action. */
  tag_id?: string | null;
  /** Transcript-only context; the server verifies matching AI history. */
  completion_context?: 'ai_call' | 'ai_email';
  delivered?: boolean;
  shipment_id?: string;
  snooze_minutes?: string;
}

export const ResolveAlertComments = (
  token: string,
  model: ResolveAlertCommentsModel
): Promise<ApiResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost('Post', APIPaths.resloved_alert_comment, model, token)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.error('error?.response?.data?.error', error?.response);
        reject(error?.response);
      });
  });
};

//Share shipment
interface ShareShipmentPayload {
  shipment: string;
  show_product_details: boolean;
  show_alerts: boolean;
  show_sensor_data: boolean;
  hidden_vitals: TelemetryVital[];
  mask_tracking_link: boolean;
  require_password: boolean;
  password: string | null;
  token: string;
}

export const ShareShipmentApi = (
  token: string,
  model: ShareShipmentPayload
): Promise<ApiResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost(
        'Post',
        APIPaths.share_shipment,
        model,
        token,
        null,
        OperationType.Read
      )
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.error('Error while sharing shipment:', error?.response);
        reject(error?.response);
      });
  });
};

//get share external link
export const CheckPasswordAgaintUUID = (uuid: string): Promise<unknown> => {
  const url = `${APIPaths.share_shipment}${uuid}/`;
  return new Promise((resolve, reject) => {
    interceptor
      .axiosWithoutHaeders('Get', url, null)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.error('Error while sharing shipment:', error?.response);
        reject(error?.response);
      });
  });
};

//authenticate shipment
interface AuthenticatePasswordData {
  show_product_details: boolean;
  show_alerts: boolean;
}

export const authenticatePassword = (
  model: unknown
): Promise<ApiResponse<AuthenticatePasswordData>> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosWithoutHaeders('Post', APIPaths.authenticate_password, model)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.error('Error while sharing shipment:', error?.response);
        reject(error?.response);
      });
  });
};

//Resolve alerts

export const ResolvedAlert = (token: string, id: string): Promise<unknown> => {
  const url = `${APIPaths.resloved_alert}${id}/`;
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', url, token, null)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.error('error?.response?.data?.error', error?.response);
        reject(error?.response);
      });
  });
};

// Get Shipment Alerts detail
export const GetShipmentAlertsPublically = (
  id: string,
  uuid: string,
  params: string
): Promise<unknown> => {
  const url = `${APIPaths.shipment_alert}${id}/?token=${uuid}&preferred_timezone=${params}`;
  return new Promise((resolve, reject) => {
    interceptor
      .axiosWithoutHaeders('Get', url, null)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.error('error?.response?.data?.error', error?.response);
        reject(error?.response);
      });
  });
};

// Get Shipment Alerts detail
export type GetShipmentAlertsResponse = ApiResponse<ShipmentAlertData[]>;

export const GetShipmentAlerts = (
  token: string,
  id: string,
  uuid?: string
): Promise<GetShipmentAlertsResponse> => {
  const base = `${APIPaths.shipment_alert}${id}/`;
  const url = uuid ? `${base}?token=${encodeURIComponent(uuid)}` : base;
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', url, token, null)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.error('error?.response?.data?.error', error?.response);
        reject(error?.response);
      });
  });
};

export interface AlertOwnerOptionsData {
  roles: Array<{
    key: string;
    label: string;
    display_label: string;
    available: boolean;
    disabled_reason?: string;
  }>;
  teams: Array<{
    id: string;
    name: string;
    color?: string;
    users: Array<{ id: string; name: string; email?: string }>;
    disabled: boolean;
    disabled_reason?: string;
  }>;
}

export interface AlertAssignmentPayload {
  expected_version: number;
  client_action_id: string;
  tag_id?: string | null;
  comment?: string;
  selections: Array<
    { type: 'user' | 'team'; id: string } | { type: 'role'; key: string }
  >;
}

export const GetAlertOwnerOptions = (
  token: string,
  shipmentId: string
): Promise<ApiResponse<AlertOwnerOptionsData>> =>
  interceptor.axiosGet(
    'Get',
    APIPaths.alertOwnerOptions(shipmentId),
    token,
    null
  ) as Promise<ApiResponse<AlertOwnerOptionsData>>;

export const AssignShipmentAlert = (
  token: string,
  alertId: string,
  payload: AlertAssignmentPayload,
  files: File[] = []
): Promise<
  ApiResponse<{ version: number; assignment: AlertAssignmentSnapshot | null }>
> => {
  const request = (() => {
    if (files.length === 0) {
      return interceptor.axiosPost(
        'Post',
        APIPaths.alertAssignment(alertId),
        payload,
        token
      );
    }

    const formData = new FormData();
    formData.append('expected_version', String(payload.expected_version));
    formData.append('client_action_id', payload.client_action_id);
    if (payload.tag_id) formData.append('tag_id', payload.tag_id);
    formData.append('comment', payload.comment ?? '');
    formData.append('selections', JSON.stringify(payload.selections));
    files.forEach((file) => formData.append('files', file, file.name));
    return interceptor.axiosPostForUpload(
      'Post',
      APIPaths.alertAssignment(alertId),
      formData,
      token
    );
  })();

  return request as Promise<
    ApiResponse<{
      version: number;
      assignment: AlertAssignmentSnapshot | null;
    }>
  >;
};

export type AlertActivitySeverity = 'critical' | 'at_risk';

/** Per-shipment alert summary returned on every row of the shipment list. */
export interface ShipmentAlertActivitySummary {
  available: boolean;
  /** Open alerts only. */
  alert_count: number;
  /** Open, resolved and dismissed alerts. */
  total_alert_count: number;
  highest_severity: AlertActivitySeverity | null;
  has_unread: boolean;
  activity_version: number;
}

export interface AlertActivityOwner {
  id: string;
  name: string;
  email?: string | null;
}

export type AlertActivityState = 'OPEN' | 'RESOLVED' | 'DISMISSED';

export type AlertActivityAction = 'assign' | 'resolve' | 'dismiss';

export interface AlertActivityRow {
  /** Use this for assign, resolve and dismiss calls, not event_id. */
  alert_id: string;
  event_id: string | null;
  event_key: string;
  alert_type: string;
  title: string;
  occurred_at: string;
  severity: AlertActivitySeverity;
  state: AlertActivityState;
  /** e.g. open, processing, needs_attention, dismissed, resolved, resolved_via_ai */
  lifecycle: string;
  owners: AlertActivityOwner[];
  assignment_enabled: boolean;
  /** Resolution tag, once the backend fills it in. */
  close_tag: { id?: string | null; label: string } | null;
  comment_count: number;
  actions: AlertActivityAction[];
}

export interface AlertActivityPage {
  available: boolean;
  shipment_id: string;
  activity_version: number;
  results: AlertActivityRow[];
  next_cursor: string | null;
  has_more: boolean;
}

export interface AlertActivitySeenPayload {
  /** The activity_version the user was shown. */
  version: number;
  /** true only when an alert panel is explicitly opened, never for polling. */
  acknowledge: boolean;
}

export interface AlertActivitySeenResult {
  activity_version: number;
  seen_version: number;
  acknowledged: boolean;
  persisted: boolean;
}

export const GetShipmentAlertActivity = (
  token: string,
  shipmentId: string,
  cursor?: string | null
): Promise<ApiResponse<AlertActivityPage>> => {
  const query = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
  return interceptor.axiosGet(
    'Get',
    `${APIPaths.shipmentAlertActivity(shipmentId)}${query}`,
    token,
    null
  ) as Promise<ApiResponse<AlertActivityPage>>;
};

export const MarkShipmentAlertActivitySeen = (
  token: string,
  shipmentId: string,
  payload: AlertActivitySeenPayload
): Promise<ApiResponse<AlertActivitySeenResult>> =>
  interceptor.axiosPost(
    'Post',
    APIPaths.shipmentAlertActivitySeen(shipmentId),
    payload,
    token
  ) as Promise<ApiResponse<AlertActivitySeenResult>>;

/**
 * Acknowledge the shipment's alert activity for the current user. Use it only
 * when the user explicitly opens an alert panel, never for polling or
 * prefetch: it records the acknowledgement time behind the KPI widgets. The
 * backend caps the version at the current one, so read it first.
 */
export const AcknowledgeShipmentAlertActivity = async (
  token: string,
  shipmentId: string
): Promise<ApiResponse<AlertActivitySeenResult>> => {
  const page = await GetShipmentAlertActivity(token, shipmentId);
  return MarkShipmentAlertActivitySeen(token, shipmentId, {
    version: page.data.data.data.activity_version,
    acknowledge: true,
  });
};

//Api for bulk upload
export const UploadShipmentFile = (
  token: string,
  model: unknown
): Promise<unknown> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPostForUpload('Post', APIPaths.uploadBulkShipment, model, token)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.error('error?.response?.data?.error', error?.response);
        reject(error?.response);
      });
  });
};

//upload file api
export const uploadFile = (
  token: string,
  model: FormData
): Promise<ApiResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPostForUpload('Post', APIPaths.uploadFile, model, token)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.error('error?.response?.data?.error', error?.response);
        reject(error?.response);
      });
  });
};

//Api for download Template

export const DownloadBulkShipmentTemplate = (
  token: string
): Promise<unknown> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGetDownload('Get', APIPaths.downloadBulkUploadTemplate, token, null)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.error('error?.response?.data?.error', error?.response);
        reject(error?.response);
      });
  });
};

// archive shipment
export const ArchiveShipment = (
  id: string,
  token: string
): Promise<ApiResponse> => {
  const url = `${APIPaths.shipment_archive}`;
  return new Promise((resolve, reject) => {
    interceptor
      .axiosDelete('Delete', url, id, token)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.error('error?.response?.data?.error', error?.response);
        reject(error?.response);
      });
  });
};

// mark as delivered shipment

interface MarkAsDeliveredPayload {
  delivery_date_option?: string | null;
  fallback_option?: string | null;
  custom_delivery_date?: string;
  fallback_custom_date?: string;
  timezone?: string;
  shipment_id?: string;
  shipment_ids?: string[];
  action?: string;
}

export const MarkAsDelivered = (
  token: string,
  model: MarkAsDeliveredPayload
): Promise<ApiResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost('Post', APIPaths.shipment_mark_as_delivered, model, token)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.error('error?.response?.data?.error', error?.response);
        reject(error?.response);
      });
  });
};

interface InitiateAiCallResponse {
  status?: string;
  data?: {
    data?: {
      message?: string;
    };
  };
}

export const initiateAiCall = (
  payload: unknown,
  token: string
): Promise<InitiateAiCallResponse> => {
  const baseDomain = process.env.REACT_APP_WEB_AI;
  return axios
    .post(`${baseDomain}api/shipment/initiate-ai-call-response/`, payload, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    })
    .then((res) => res.data)
    .catch((error) => {
      console.error('AI Call Initiation Error:', error?.response || error);
      throw error?.response || error;
    });
};

interface InitiateAiEmailResponse {
  status?: string;
  data?: {
    data?: {
      message?: string;
    };
  };
}

export const initiateAiEmail = (
  payload: unknown,
  token: string
): Promise<InitiateAiEmailResponse> => {
  const baseDomain = process.env.REACT_APP_WEB_AI;
  return axios
    .post(`${baseDomain}api/shipment/initiate-ai-email-response/`, payload, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    })
    .then((res) => res.data)
    .catch((error) => {
      console.error('AI Email Initiation Error:', error?.response || error);
      throw error?.response || error;
    });
};

interface InitiateTestAiCallResponse {
  message?: string;
}

export interface InitiateTestAiCallPayload {
  contact_name?: string;
  company_name?: string;
  user_notification: string;
  test_phone_number?: string | null;
  triggered_by?: string;
  company_id?: string;
}

export const initiateTestAiCall = (
  payload: InitiateTestAiCallPayload
): Promise<InitiateTestAiCallResponse> => {
  const baseDomain = process.env.REACT_APP_WEB_AI;
  return axios
    .post(`${baseDomain}ai/notification/call/preview/`, payload, {
      headers: {
        'Content-Type': 'application/json',
      },
    })
    .then((res) => res.data)
    .catch((error) => {
      console.error('AI Call Initiation Error:', error?.response || error);
      throw error?.response || error;
    });
};

export interface DontShowMessageResponse {
  data?: {
    data?: {
      is_success: boolean;
      message: string;
      data?: {
        access: string;
        refresh: string;
      };
    };
  };
}

interface DontShowMessagePayload {
  ai_static_message?: boolean;
  ai_email_static_message?: boolean;
}

export const dontShowMessage = (
  token: string,
  payload: DontShowMessagePayload
): Promise<DontShowMessageResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost('Post', APIPaths.noMessage, payload, token)
      .then((res) => resolve(res))
      .catch((error) => {
        console.error('AI Call Initiation Error:', error?.response);
        reject(error?.response);
      });
  });
};

export const oneTimeResolve = (
  token: string,
  alertId: string
): Promise<ApiResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost('Post', `${APIPaths.oneTimeResolve}${alertId}/`, null, token)
      .then((res) => resolve(res))
      .catch((error) => {
        console.error('One-time Resolve Error:', error?.response);
        reject(error?.response);
      });
  });
};

export const oneTimeDismiss = (
  token: string,
  alertId: string
): Promise<ApiResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost('Post', `${APIPaths.oneTimeDismiss}${alertId}/`, null, token)
      .then((res) => resolve(res))
      .catch((error) => {
        console.error('One-time Dismiss Error:', error?.response);
        reject(error?.response);
      });
  });
};

export interface CallTranscriptMessage {
  sender: string;
  text: string;
}
interface SentimentAnalysis {
  type: 'positive' | 'negative' | 'neutral' | string;
  label: string;
  color: string;
  icon?: string;
}

export interface CallTranscriptData {
  author_badge_color?: string;
  is_critical: boolean;
  is_escalation_call?: boolean;
  title?: string;
  status_info?: StatusInfo;
  triggered_by_user_name?: string;
  triggered_by?: string;
  recipients?: Recipient[];
  sentiment_analysis?: SentimentAnalysis;
  message_content?: string;
  alert_id?: string;
  summary?: string;
  next_step?: string;
  contact_info?: ContactInfo;
  audio_url?: string;
  show_buttons?: boolean;
  transcript?: CallTranscriptMessage[];
  agent_name?: string;
  agent_id?: string | null;
  agent_purpose?: string | null;
  primary_contact_name?: string;
  company_user_preferred_language?: string;
  timestamp?: string;
  call_duration?: string;
  statusDescription?: string;
  comments?: ActivityItem;
  bound_caller?: string;
  alert_title?: string;
}

type GetCallTranscriptResponse = ApiResponse<CallTranscriptData>;

export const getCallTranscriptOneTime = (
  token: string,
  alertId: string,
  isChecked: boolean,
  shipmentId: string | null | undefined
): Promise<GetCallTranscriptResponse> => {
  const shipmentParam = shipmentId != null ? `&shipment_id=${shipmentId}` : '';
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet(
        'Get',
        `${APIPaths.callTranscriptOneTime}${alertId}/?translate_into_user_preferred_language=${isChecked}${shipmentParam}`,
        token
      )
      .then((res) => resolve(res))
      .catch((error) => {
        console.error('Call Transcript Fetch Error:', error?.response);
        reject(error?.response);
      });
  });
};

interface SentimentInfo {
  type: string;
}

interface StatusInfo {
  description: string;
  label: string;
  sentiment?: SentimentInfo;
}

interface ContactInfo {
  name?: string;
  phone?: string;
}

interface EmailTranscriptMessage {
  sender: string;
  from: string;
  from_name?: string;
  to: string;
  body: string;
  timestamp: string;
  direction?: string;
}

interface Recipient {
  id: string;
  name: string;
  role?: string;
}

export interface EmailTranscriptData {
  is_critical?: boolean;
  author_badge_color?: string;
  title?: string;
  triggered_by_user_name?: string;
  triggered_by?: string;
  recipients?: Recipient[];
  message_content?: string;
  summary?: string;
  show_buttons?: boolean;
  sentiment_analysis?: SentimentAnalysis;
  alert_id?: string;
  next_step?: string;
  timestamp?: string;
  email_duration?: string;
  call_duration?: string;
  company_user_preferred_language?: string;
  status_info?: StatusInfo;
  contact_info?: ContactInfo;
  transcript?: EmailTranscriptMessage[];
  comments?: ActivityItem;
  alert_title?: string;
  ai_status?: string;
  follow_up?: { direction?: string };
  agent_id?: string | null;
  agent_name?: string;
  agent_purpose?: string | null;
  /** Reply-to-AI-emails block (contract v0.3). Present only for companies with the feature. */
  native?: EmailPanelNative;
  email_replies?: EmailReplyData[];
}

export type GetEmailTranscriptOneTimeResponse =
  ApiResponse<EmailTranscriptData>;

export const getEmailTranscriptOneTime = (
  token: string,
  alertId: string,
  isChecked: boolean,
  shipmentId: string | null | undefined
): Promise<GetEmailTranscriptOneTimeResponse> => {
  const shipmentParam = shipmentId != null ? `&shipment_id=${shipmentId}` : '';
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet(
        'Get',
        `${APIPaths.emailTranscriptOneTime}${alertId}/?translate_into_user_preferred_language=${isChecked}${shipmentParam}`,
        token
      )
      .then((res) => resolve(res))
      .catch((error) => {
        console.error('Email Transcript Fetch Error:', error?.response);
        reject(error?.response);
      });
  });
};

export const getCallTranscript = (
  token: string,
  alertId: string,
  isChecked: boolean
): Promise<GetCallTranscriptResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet(
        'Get',
        `${APIPaths.callTranscript}${alertId}/?translate_into_user_preferred_language=${isChecked}`,
        token
      )
      .then((res) => resolve(res))
      .catch((error) => {
        console.error('Call Transcript Fetch Error:', error?.response);
        reject(error?.response);
      });
  });
};

export const getCallTranscriptCompanyAlert = (
  token: string,
  commentId: string,
  isChecked: boolean
): Promise<GetCallTranscriptResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet(
        'Get',
        `${APIPaths.callTranscriptCompanyAlert}${commentId}/?translate_into_user_preferred_language=${isChecked}`,
        token
      )
      .then((res) => resolve(res))
      .catch((error) => {
        console.error(
          'Company Alert Call Transcript Fetch Error:',
          error?.response
        );
        reject(error?.response);
      });
  });
};

type GetEmailTranscriptResponse = ApiResponse<EmailTranscriptData>;

export const getEmailTranscript = (
  token: string,
  alertId: string,
  isChecked: boolean,
  // The thread the user clicked Reply on (reply-to-AI-emails); omitted for
  // every other caller, which keeps the request exactly as it was.
  threadId?: string | null
): Promise<GetEmailTranscriptResponse> => {
  const threadParam = threadId
    ? `&thread_id=${encodeURIComponent(threadId)}`
    : '';
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet(
        'Get',
        `${APIPaths.emailTranscript}${alertId}/?translate_into_user_preferred_language=${isChecked}${threadParam}`,
        token
      )
      .then((res) => resolve(res))
      .catch((error) => {
        console.error('Email Transcript Fetch Error:', error?.response);
        reject(error?.response);
      });
  });
};

export const GetSingleOneTimeNotification = (
  token: string,
  id: string
): Promise<ApiResponse<SingleShipmentDataExtended>> => {
  const url = `${APIPaths.shipmentNotificationListing}${id}/`;

  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', url, token, null)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.error(error?.response);
        reject(error?.response);
      });
  });
};

export const shipmentAlertAiStatus = (
  token: string,
  payload: unknown
): Promise<ApiResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost(
        'Post',
        APIPaths.shipmentAlertStatus,
        payload,
        token,
        null,
        OperationType.Read
      )
      .then((res) => resolve(res))
      .catch((error) => {
        console.error('AI Call Initiation Error:', error?.response);
        reject(error?.response);
      });
  });
};
export const reorderTabs = (
  token: string,
  payload: unknown
): Promise<ApiResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost('Post', APIPaths.reorderPinTabs, payload, token)
      .then((res) => resolve(res))
      .catch((error) => {
        console.error('AI Call Initiation Error:', error?.response);
        reject(error?.response);
      });
  });
};

interface TerminateAiEmailPayload {
  alert_id: string;
  reason?: string;
  triggered_by?: number;
}

interface TerminateAiEmailResponseData {
  alert_id: string;
  terminated_at: string;
  ai_status?: string;
  use_ai?: boolean;
  external_service_notified?: boolean;
  warning?: string;
}

export const terminateAiEmail = (
  token: string,
  payload: TerminateAiEmailPayload
): Promise<ApiResponse<TerminateAiEmailResponseData>> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost('Post', APIPaths.terminateAiEmail, payload, token)
      .then((res) => resolve(res.data))
      .catch((error) => {
        console.error('Terminate AI Email Error:', error?.response);
        reject(error?.response);
      });
  });
};

export interface EmailReplyAttachment {
  filename: string;
  content: string;
  content_type: string;
}

export interface EmailReplySentBy {
  id: string;
  name: string;
  email: string;
}

export interface EmailReplyData {
  id: string;
  body: string;
  sent_by: EmailReplySentBy;
  sent_at: string;
  status: 'sent' | 'failed' | 'pending';
  /** The reply API (ZOO-694) also returns these; the old list may not. */
  to?: string;
  cc?: string[];
  attachment_names?: string[];
}

export const postEmailReply = (
  token: string,
  alertId: string,
  body: string,
  attachments: EmailReplyAttachment[]
): Promise<EmailReplyData> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost(
        'Post',
        `${APIPaths.alertEmailReplies}${alertId}/email-replies/`,
        { body, attachments },
        token
      )
      .then((res) => resolve(res.data?.data?.data))
      .catch((error) => {
        reject(error?.response);
      });
  });
};

export const postOneTimeEmailReply = (
  token: string,
  eventId: string,
  body: string,
  attachments: EmailReplyAttachment[]
): Promise<EmailReplyData> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost(
        'Post',
        `${APIPaths.oneTimeEmailReplies}${eventId}/email-replies/`,
        { body, attachments },
        token
      )
      .then((res) => resolve(res.data?.data?.data))
      .catch((error) => {
        reject(error?.response);
      });
  });
};

export const shipmentNotificationListing = (
  token: string,
  shipmentId: string
): Promise<unknown> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet(
        'Get',
        `${APIPaths.shipmentNotificationListing}?shipment_id=${shipmentId}`,
        token
      )
      .then((res) => resolve(res))
      .catch((error) => {
        console.error('Email Transcript Fetch Error:', error?.response);
        reject(error?.response);
      });
  });
};
export const deleteShipmentNotification = (
  id: string,
  token: string
): Promise<unknown> => {
  const url = `${APIPaths.addShipmentNotification}${id}/`;
  return new Promise((resolve, reject) => {
    interceptor
      .axiosDelete('Delete', url, null, token) // No payload for DELETE
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.error('error?.response?.data?.error', error?.response);
        reject(error?.response);
      });
  });
};

export const updateShipmentNotification = (
  token: string,
  model: unknown,
  notificationId: string
): Promise<unknown> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPut(
        'Put',
        `${APIPaths.addShipmentNotification}${notificationId}/`,
        model,
        token
      )
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.error('error?.response?.data?.error', error?.response);
        reject(error?.response);
      });
  });
};

export const createShipmentNotification = (
  token: string,
  model: unknown
): Promise<unknown> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost('Post', APIPaths.addShipmentNotification, model, token)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.error('error?.response?.data?.error', error?.response);
        reject(error?.response);
      });
  });
};

interface MuteShipmentNotificationsYourselfResponse {
  data?: {
    data?: {
      data?: { mute_status: boolean };
      is_success: boolean;
      message: string;
    };
  };
}

interface MuteShipmentNotificationsYourselfModel {
  shipment: string;
  mute_for_yourself: boolean;
}

export const muteShipmentNotificationsYourself = (
  token: string,
  model: MuteShipmentNotificationsYourselfModel
): Promise<MuteShipmentNotificationsYourselfResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost('Post', APIPaths.muteNotificationForYourself, model, token)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.error('error?.response?.data?.error', error?.response);
        reject(error?.response);
      });
  });
};

interface SendToItem {
  id: string;
  select_all: boolean;
  users: string[];
  create_self: boolean;
}

// calendar select-all: backend resolves the shipments itself from these keys
export interface NotificationBulkScope {
  select_all: true;
  exclude_shipment_ids: string[];
  filters: Record<string, unknown>;
  start?: string;
  end?: string;
  view_by?: string;
}

export interface OneTimeNotificationPayload extends Partial<NotificationBulkScope> {
  call_contact_id?: string;
  call_agent_id?: string;
  message: {
    body_html: string;
    body_text: string;
  };
  send_now: boolean;
  notification_method: string;
  timezone: string;
  additional_recipients: string[];
  scheduled_at: string | null;
  shipment_ids?: string | string[] | (string | string[])[];
  send_to: SendToItem[];
  bulk?: boolean;
  id: string | null;
}

export interface OneTimeCallChoices {
  contacts: { id: string; name: string }[];
  agents: { id: string; name: string }[];
}

export const getOneTimeCallChoices = async (
  token: string
): Promise<OneTimeCallChoices> => {
  const response = await interceptor.axiosGet(
    'Get',
    `${APIPaths.one_time}call-choices/`,
    token
  );
  return response.data.data.data;
};

export const createOneTimeShipmentNotification = (
  token: string,
  model: OneTimeNotificationPayload,
  id: string | null = null
): Promise<ApiResponse> => {
  const isEdit = Boolean(id);
  const url = isEdit ? `${APIPaths.one_time}${id}/` : APIPaths.one_time;

  return new Promise((resolve, reject) => {
    const request = isEdit
      ? interceptor.axiosPut('Put', url, model, token)
      : interceptor.axiosPost('Post', url, model, token);

    request
      .then((res) => resolve(res))
      .catch((error) => {
        console.error('error?.response?.data?.error', error?.response);
        reject(error?.response);
      });
  });
};

export const previewOneTimeEmailNotification = (
  token: string,
  model: unknown
): Promise<unknown> => {
  const url = `${APIPaths.one_time}preview-email/`;

  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost('Post', url, model, token)
      .then((res) => resolve(res))
      .catch((error) => {
        console.error('error?.response?.data?.error', error?.response);
        reject(error?.response);
      });
  });
};

export interface MessageHistoryItem {
  original_message: string;
  send_count: number;
  last_sent_at: string;
}

export interface MessageHistoryResponse {
  data: {
    data: {
      data: MessageHistoryItem[];
    };
  };
}

export const getOneTimeMessageHistory = (
  token: string
): Promise<MessageHistoryResponse> => {
  const url = `${APIPaths.one_time}message-history/`;

  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', url, token, null)
      .then((res) => resolve(res))
      .catch((error) => {
        console.error('error fetching message history', error?.response);
        reject(error?.response);
      });
  });
};

interface ShipmentLinkCodesResponse {
  data: {
    data: {
      data: {
        link_codes: string[];
      };
    };
  };
}

export const GetShipmentLinkCodes = (
  token: string
): Promise<ShipmentLinkCodesResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', APIPaths.get_link_code, token, null)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.error(error?.response);
        reject(error?.response);
      });
  });
};

export interface ShipmentRtgCodesData {
  shipper_rtg: string[];
  consignee_rtg: string[];
}

export type ShipmentRtgCodesResponse = ApiResponse<ShipmentRtgCodesData>;

export const GetShipmentRtgCodes = (
  token: string
): Promise<ShipmentRtgCodesResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', APIPaths.get_rtg_codes, token, null)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.error(error?.response);
        reject(error?.response);
      });
  });
};

export interface ShipmentNotificationRecipientData {
  additional?: AdditionalRecipientOption[];
  teams_users?: TeamWithUsers[];
}

export type ShipmentNotificationRecipientResponse =
  ApiResponse<ShipmentNotificationRecipientData>;

export const shipmentNotificationRecipient = (
  token: string,
  shipmentId: string
): Promise<ShipmentNotificationRecipientResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet(
        'Get',
        `${APIPaths.notificationRecipient}?shipment_id=${shipmentId}`,
        token
      )
      .then((res) => resolve(res))
      .catch((error) => {
        console.error('Email Transcript Fetch Error:', error?.response);
        reject(error?.response);
      });
  });
};

export interface AlertRecipient {
  id: number | string;
  email: string;
  name: string;
}

export type ShipmentAlertRecipientResponse = ApiResponse<AlertRecipient[]>;

export const GetGeoFenceStops = (
  token: string,
  shipmentId: string,
  page = 1
): Promise<GeoFenceStopsResponse> => {
  const url = `${APIPaths.geoFenceStops(shipmentId)}?page=${page}`;

  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', url, token)
      .then((res) => resolve(res as GeoFenceStopsResponse))
      .catch((error) => {
        console.error('Geo-fence stops fetch error:', error?.response);
        reject(error?.response);
      });
  });
};

export const SaveShipmentGeoFence = (
  token: string,
  shipmentId: string,
  stopKey: string,
  model: SaveGeoFencePayload
): Promise<SaveGeoFenceResponse> => {
  const url = APIPaths.shipmentGeoFence(shipmentId, stopKey);

  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost('Post', url, model, token)
      .then((res) => resolve(res as SaveGeoFenceResponse))
      .catch((error) => {
        console.error('Geo-fence save error:', error?.response);
        reject(error?.response);
      });
  });
};

export const GetShipmentGeoFence = (
  token: string,
  shipmentId: string,
  stopKey: string
): Promise<ShipmentGeoFenceResponse> => {
  const url = APIPaths.shipmentGeoFence(shipmentId, stopKey);

  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', url, token)
      .then((res) => resolve(res as ShipmentGeoFenceResponse))
      .catch((error) => {
        console.error('Geo-fence fetch error:', error?.response);
        reject(error?.response);
      });
  });
};

export const DeleteShipmentGeoFence = (
  token: string,
  shipmentId: string,
  stopKey: string
): Promise<ApiResponse<unknown>> => {
  const url = APIPaths.shipmentGeoFence(shipmentId, stopKey);

  return new Promise((resolve, reject) => {
    interceptor
      .axiosDelete('Delete', url, null, token)
      .then((res) => resolve(res as ApiResponse<unknown>))
      .catch((error) => {
        console.error('Geo-fence delete error:', error?.response);
        reject(error?.response);
      });
  });
};

export type AlertRecipientPayload =
  { shipment_ids: string[]; bulk?: true } | NotificationBulkScope;

export const shipmentAlertRecipient = (
  token: string,
  payload: AlertRecipientPayload
): Promise<ShipmentAlertRecipientResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost(
        'Post',
        APIPaths.alertRecipient,
        payload,
        token,
        null,
        OperationType.Read
      )
      .then((res) => resolve(res))
      .catch((error) => {
        console.error('Alert recipients fetch error:', error?.response);
        reject(error?.response);
      });
  });
};

export interface ExternalShipmentAlertModel {
  notification_method: 'SMS' | 'EMAIL';
  contact_info?: string;
  phone_number?: string;
  eta_threshold?: number;
  ETA_CHANGE?: boolean;
  DELIVERY_EXCEPTION?: boolean;
  SHIPMENT_DELIVERED?: boolean;
  SHIPMENT_OUT_FOR_DELIVERY: boolean;
  token: string;
  alert_types: string[];
}

export type IssueType = 'DELIVERY_TIME_PREFERENCE' | 'DELIVERY_ADDRESS_CHANGE';

export interface ExternalReportIssueModel {
  token: string;
  issue_type: IssueType;
  submitter_channel: string | null;
  submitter_identifier: string | null;
  timezone: string;

  requested_delivery_date?: string;
  requested_window_start_hour?: number;
  requested_window_end_hour?: number;
}

export const externalShipmentAlert = (
  model: ExternalShipmentAlertModel
): Promise<ApiResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost('Post', APIPaths.setExternalAlert, model)
      .then((res) => resolve(res))
      .catch((error) => {
        console.error('Set alert Error:', error?.response);
        reject(error?.response);
      });
  });
};

export const externalLinkReportIssue = (
  model: ExternalReportIssueModel
): Promise<ApiResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost('Post', APIPaths.external_link_report_issues, model)
      .then((res) => resolve(res))
      .catch((error) => {
        console.error('External report issue Error:', error?.response);
        reject(error?.response);
      });
  });
};
//bulk shipment apis
export const uploadBulkShipment = (
  token: string,
  model: unknown
): Promise<unknown> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPostForUpload('Post', APIPaths.bulkUpload, model, token)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.error('error?.response?.data?.error', error?.response);
        reject(error?.response);
      });
  });
};

// TODO: improve this type annotation
type ColumnMatchingResponse = ApiResponse<{ [key: string]: unknown }[]>;

export const columnMatching = (
  token: string,
  model: unknown
): Promise<ColumnMatchingResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost('Post', APIPaths.bulkValidate, model, token)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.error('error?.response?.data?.error', error?.response);
        reject(error?.response);
      });
  });
};

export const bulkCreate = (
  token: string,
  model: unknown
): Promise<ApiResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost('Post', APIPaths.bulkCreate, model, token)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        reject(error?.response);
      });
  });
};

export type MassActionType =
  | 'mark_as_delivered'
  | 'mark_as_delivered_preflight'
  | 'dismiss_all_alerts'
  | 'archive'
  | 'update_teams'
  | 'update_teams_preflight';

interface ShipmentMassActionsPayload {
  action: MassActionType;
  shipment_ids?: string[];
  select_all?: boolean;
  exclude_shipment_ids?: string[];
  filters?: Record<string, unknown>;
  add_team_ids?: string[];
  remove_team_ids?: string[];
}

export const shipmentMassActions = (
  token: string,
  model: ShipmentMassActionsPayload
): Promise<ApiResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost('Post', APIPaths.mass_actions, model, token)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.error('error?.response?.data?.error', error?.response);
        reject(error?.response);
      });
  });
};

export const listNotificationRecipient = (
  token: string,
  shipmentId: string
): Promise<unknown> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet(
        'Get',
        `${APIPaths.listRecipients}?shipment_id=${shipmentId}`,
        token
      )
      .then((res) => resolve(res))
      .catch((error) => {
        console.error('Email Transcript Fetch Error:', error?.response);
        reject(error?.response);
      });
  });
};

export const ReplyComment = (
  token: string,
  model: FormData
): Promise<ApiResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPostForUpload(
        'Post',
        `${APIPaths.replyComment}messages/`,
        model,
        token
      )
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.error(
          'error?.response white replay a comment',
          error?.response
        );
        reject(error?.response);
      });
  });
};

export interface MentionUser {
  id: string;
  name: string;
  email: string;
  author_badge_color?: string;
  badge: string;
  badge_color?: string;
}

export interface FetchMentionUsersResponse {
  data: {
    data: {
      data: MentionUser[];
      is_success: boolean;
      message?: string;
      status_code?: number;
      page_count?: number;
      total_count?: number;
    };
  };
}

export const fetchMentionUsers = (
  token: string,
  shipmentId?: string,
  page?: number,
  search?: string,
  options?: { logiblocks?: boolean }
): Promise<FetchMentionUsersResponse> => {
  return new Promise((resolve, reject) => {
    const params = new URLSearchParams();
    if (shipmentId) {
      params.append('shipment_id', shipmentId);
    }

    if (page != null) {
      params.append('page', page.toString());
    }

    if (search) {
      params.append('q', search);
    }

    // When true, backend includes @Controlling office sentinel (Alert-event
    // Logiblocks only — callers should gate on alertTrigger before setting).
    if (options?.logiblocks) {
      params.append('logiblocks', '1');
    }
    const queryString = params.toString();
    const url = `${APIPaths.replyComment}users/search/${
      queryString ? `?${queryString}` : ''
    }`;

    interceptor
      .axiosGet('Get', url, token)
      .then((res) => resolve(res))
      .catch((error) => {
        console.error('Mention Users Fetch Error:', error?.response);
        reject(error?.response);
      });
  });
};

export const checkMentionUsers = (
  token: string,
  query: string
): Promise<FetchMentionUsersResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', `${APIPaths.checkMentionsUsers}${query}`, token)
      .then((res) => resolve(res))
      .catch((error) => {
        console.error('Mention Users Fetch Error:', error?.response);
        reject(error?.response);
      });
  });
};

export const fetchShipmentTeams = (
  token: string,
  shipmentId: string
): Promise<FetchMentionUsersResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet(
        'Get',
        `${APIPaths.shipmentTeams}?shipment_id=${shipmentId}`,
        token
      )
      .then((res) => resolve(res))
      .catch((error) => {
        console.error('Shipment Teams Fetch Error:', error?.response);
        reject(error?.response);
      });
  });
};

export interface ShipmentMessage {
  message_id: string;
  type: 'mention' | 'following';
  kind?: 'mention' | 'following';
  is_read: boolean;
  user: {
    name: string;
    initials: string;
    email?: string;
    badge_color?: string;
  };
  mentioned_users?: {
    name?: string;
    initials?: string;
    email?: string;
    badge_color?: string;
  }[];
  shipment: {
    id: string;
    reference_number: string;
  };
  referenced_object: {
    type?: string;
    id: string;
  };
  timestamp: string;
  content: string;
  audience?: string;
  audience_display?: string;
  has_attachments?: boolean;
  attachment_count?: number;
}

export interface NotificationGroup {
  date: string;
  messages: ShipmentMessage[];
  unread_count: number;
}

export interface NotificationUnreadCounts {
  all: number;
  total: number;
  mentions: number;
}

interface FetchAllNotificationsResponseData {
  message: string;
  status: number;
  is_success: boolean;
  messageTypeId: string;
  data: NotificationGroup[];
  page_count: number;
  unread_counts: NotificationUnreadCounts;
}

interface FetchAllNotificationsResponseBody {
  data: FetchAllNotificationsResponseData;
  message: {
    text: string;
    title: string;
    messageTypeId: string;
  };
}

type FetchAllNotificationsResponse =
  AxiosResponse<FetchAllNotificationsResponseBody>;

export const fetchAllNotifications = (
  token: string,
  filter?: 'mentions'
): Promise<FetchAllNotificationsResponse> => {
  const url = filter
    ? `${APIPaths.replyComment}inbox/?filter=${filter}`
    : `${APIPaths.replyComment}inbox/`;

  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', url, token)
      .then((res) => resolve(res))
      .catch((error) => {
        console.error('Email Transcript Fetch Error:', error?.response);
        reject(error?.response);
      });
  });
};

export interface MarkAsReadPayload {
  message_ids: (string | null)[];
  mark_all: boolean;
}

export const notificationMarkAsRead = (
  token: string,
  model: MarkAsReadPayload
): Promise<unknown> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost('Post', `${APIPaths.replyComment}inbox/`, model, token)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.error('error?.response?.data?.error', error?.response);
        reject(error?.response);
      });
  });
};

export interface MarkShipmentCocCommentsReadPayload {
  comment_ids?: string[];
}

export interface MarkShipmentCocCommentsReadResponse {
  updated: number;
}

export const markShipmentCocCommentsRead = (
  token: string,
  shipmentId: string,
  model?: MarkShipmentCocCommentsReadPayload
): Promise<ApiResponse<MarkShipmentCocCommentsReadResponse>> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost(
        'Post',
        APIPaths.markShipmentCocCommentsRead(shipmentId),
        model ?? {},
        token,
        null,
        OperationType.SilentWrite
      )
      .then((res) =>
        resolve(res as ApiResponse<MarkShipmentCocCommentsReadResponse>)
      )
      .catch((error) => {
        console.error('mark shipment coc comments read error', error?.response);
        reject(error?.response);
      });
  });
};

interface COCRetryCallModel {
  reason: string;
}

export const COCRetryCall = (
  token: string,
  model: COCRetryCallModel,
  id: string
): Promise<ApiResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost('Post', `${APIPaths.retry_call}${id}/retry/`, model, token)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.error('error?.response?.data?.error', error?.response);
        reject(error?.response);
      });
  });
};

export const COCAlertRetryCall = (
  token: string,
  model: COCRetryCallModel,
  id: string
): Promise<ApiResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost('Post', `${APIPaths.alert_retry_call}${id}/`, model, token)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.error('error?.response?.data?.error', error?.response);
        reject(error?.response);
      });
  });
};

export const GetDownloadReportShipments = (
  token: string,
  params: string
): Promise<unknown> => {
  const queryString = new URLSearchParams(params).toString();
  const baseUrl = APIPaths.download_report_shipments;
  const url = queryString ? `${baseUrl}?${queryString}` : baseUrl;
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', url, token)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.error('Error fetching shipments:', error?.response);
        reject(error?.response);
      });
  });
};

// BEFORE: GetDownloadReportShipments(token, queryString)
// AFTER (new signature): GetDownloadReportShipments(token, body)

export const PostDownloadReportShipments = (
  token: string,
  body: FiltersPayload
) => {
  const safeBody = body ?? {};

  return interceptor
    .axiosPost('Post', APIPaths.download_report, safeBody, token)
    .then((res) => res)
    .catch((error) => {
      // Keep your existing logging style
      console.error(
        'Error fetching shipments (POST):',
        error?.response || error
      );
      return Promise.reject(error?.response || error);
    });
};

/**
 * NEW: Get Pre-Calculated Report with Pagination
 * This endpoint loads data from the pre-calculated report table (fast!)
 */
export const GetPreCalculatedReport = (
  token: string,
  body: FiltersPayload & {
    page?: number;
    page_size?: number;
    preset_id?: string;
    search?: string;
  }
) => {
  const safeBody = body ?? {};
  return interceptor
    .axiosPost(
      'Post',
      APIPaths.pre_calculated_report,
      safeBody,
      token,
      null,
      OperationType.Read
    )
    .then((res) => res)
    .catch((error) => {
      console.error(
        'Error fetching pre-calculated report:',
        error?.response || error
      );
      return Promise.reject(error?.response || error);
    });
};

/**
 * NEW: Trigger Report Re-calculation
 * Starts a Celery task to recalculate report data for a date range
 */
export const TriggerReportRecalculate = (
  token: string,
  body: { start_date?: string; end_date?: string; timezone_format?: string }
) => {
  return interceptor
    .axiosPost(
      'Post',
      APIPaths.report_recalculate,
      body,
      token,
      null,
      OperationType.Read
    )
    .then((res) => res)
    .catch((error) => {
      console.error(
        'Error triggering recalculation:',
        error?.response || error
      );
      return Promise.reject(error?.response || error);
    });
};

/**
 * NEW: Get Report Task Status
 * Poll the status of a report calculation task
 */
export const GetReportTaskStatus = (token: string, taskId?: string) => {
  const url = taskId
    ? `${APIPaths.report_task_status}?task_id=${taskId}`
    : APIPaths.report_task_status;

  return interceptor
    .axiosGet('Get', url, token)
    .then((res) => res)
    .catch((error) => {
      console.error('Error fetching task status:', error?.response || error);
      return Promise.reject(error?.response || error);
    });
};

/**
 * NEW: Download All Report Data as CSV
 * Downloads ALL data without pagination limits
 */
export const DownloadReportCSV = (token: string, body: FiltersPayload) => {
  const userTimeZone = getUserTimeZone();
  const requestUrl = `${baseUrl}${APIPaths.report_download_csv}`;

  return axios({
    method: 'POST',
    url: requestUrl,
    data: body,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      timezone: userTimeZone,
    },
    responseType: 'blob', // Important: Binary file download
  })
    .then((res) => {
      console.error('CSV download response:', {
        status: res.status,
        contentType: res.headers['content-type'],
        blobSize: res.data.size,
      });
      return res;
    })
    .catch((error) => {
      console.error('CSV download error:', error?.response);
      return Promise.reject(error?.response || error);
    });
};

export const DownloadRawData = (
  token: string,
  model: unknown
): Promise<AxiosResponse<Blob>> => {
  const method = 'Post';
  // Use the raw-data-report endpoint for generating the downloadable file
  const url = APIPaths.download_report_shipments_modal;
  const userTimeZone = getUserTimeZone();
  const requestUrl = `${baseUrl}${url}`;

  return new Promise((resolve, reject) => {
    console.error('Downloading raw data with model:', model);
    axios({
      method: method,
      url: requestUrl,
      data: model,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        timezone: userTimeZone,
      },
      responseType: 'blob', // Important: Tell axios to expect binary data
    })
      .then((res) => {
        console.error('Download response:', {
          status: res.status,
          contentType: res.headers['content-type'],
          blobSize: res.data.size,
          blobType: res.data.type,
        });
        resolve(res);
      })
      .catch((error) => {
        console.error('Download error:', error?.response);
        reject(error?.response);
      });
  });
};
interface PresetFilterInterface {
  type?: string;
  presets?: HeaderItem[];
}

export const SavePresetFilter = (
  token: string,
  model: PresetFilterInterface
): Promise<ApiResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost('Post', `${APIPaths.preset_report_filters_tabs}`, model, token)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.error('error?.response?.data?.error', error?.response);
        reject(error?.response);
      });
  });
};

export const GetPresetsFilters = (
  token: string,
  type: string
): Promise<unknown> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet(
        'Get',
        `${APIPaths.preset_report_filters_tabs}?type=${type}`,
        token
      )
      .then((res) => {
        resolve(res.data || null);
      })
      .catch((error) => {
        console.error('Error fetching shipments:', error?.response);
        reject(error?.response);
      });
  });
};

export const SaveLastUsedPresets = (
  token: string,
  id?: string,
  model?: {
    type: string;
    last_used: boolean;
    presets?: HeaderItem[];
  }
): Promise<unknown> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPut(
        'Put',
        `${APIPaths.preset_report_filters_tabs}${id}/`,
        model,
        token
      )
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.error('error?.response?.data?.error', error?.response);
        reject(error?.response);
      });
  });
};

// delete presets
export const DeletePresetFilter = (
  id: string,
  token: string
): Promise<ApiResponse> => {
  const url = `${APIPaths.preset_report_filters_tabs}${id}/`;
  return new Promise((resolve, reject) => {
    interceptor
      .axiosDelete('Delete', url, null, token)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.error('error?.response?.data?.error', error?.response);
        reject(error?.response);
      });
  });
};
export const UpdatePresetFilter = (
  id: string,
  model: PresetFilterInterface,
  token: string
): Promise<ApiResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPut(
        'Put',
        `${APIPaths.preset_report_filters_tabs}${id}/`,
        model,
        token
      )
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.error('error update preset', error?.response);
        reject(error?.response);
      });
  });
};

export const GetReportTabs = (token: string): Promise<unknown> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', `${APIPaths.report_tabs}`, token)
      .then((res) => {
        resolve(res.data || null);
      })
      .catch((error) => {
        console.error('Error fetching shipments:', error?.response);
        reject(error?.response);
      });
  });
};
// Export coc
// export const GetCOCExportCount = (
//   token: string,
//   id: string
// ): Promise<ApiResponse> => {
//   const url = `${APIPaths.coc_download}?shipment_ids=${id}`;
//   return new Promise((resolve, reject) => {
//     interceptor
//       .axiosGet('Get', url, token, null)
//       .then((res) => {
//         resolve(res);
//       })
//       .catch((error) => {
//         console.error('error?.response?.data?.error', error?.response);
//         reject(error?.response);
//       });
//   });
// };

interface coc_export_count {
  shipment_ids?: string[];
}

export const GetCOCExportCount = (
  token: string,
  model: coc_export_count
): Promise<ApiResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost(
        'Post',
        `${APIPaths.coc_download}summary/`,
        model,
        token,
        null,
        OperationType.Read
      )
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.error('error?.response?.data?.error', error?.response);
        reject(error?.response);
      });
  });
};

interface export_coc {
  shipment_ids: string[];
  include_status_updates: boolean;
  include_comments?: boolean;
  include_alerts?: boolean;
  include_pods: boolean;
}

export const DownloadCOC = async (
  token: string,
  model: export_coc
): Promise<AxiosResponse<Blob>> => {
  try {
    const requestUrl = `${baseUrl}${APIPaths.coc_download}`;

    const res = await axios({
      method: 'POST',
      url: requestUrl,
      data: model,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      responseType: 'blob',
    });
    const contentType = res.headers['content-type'];
    if (!res.data || res.data.size === 0) {
      throw new Error('Downloaded file is empty.');
    }
    if (typeof contentType === 'string' && contentType.includes('text/html')) {
      const text = await res.data.text?.();
      console.error('Server returned HTML instead of ZIP:', text);
      throw new Error(
        'Download failed: Server returned HTML (check token/API).'
      );
    }
    return res;
  } catch (error) {
    console.error('DownloadCOC error:', error);
    throw error;
  }
};

interface ProductModelData {
  shipment_id?: string;
  search?: string;
  sort_key?: string;
  sort_order?: string;
  page?: number;
  page_size?: number;
}

//     export const fetchShipmentProducts = (
//       token: string,
//       model: ProductModelData
//     ): Promise<FetchShipmentProductsResponse> => {
//   return new Promise((resolve, reject) => {
//     interceptor
//       .axiosGet(
//         'Get',
//         `${APIPaths.shipmentProducts}?shipment_id=${shipmentId}`,
//         token
//       )
//       .then((res) => resolve(res))
//       .catch((error) => {
//         console.error('Shipment Products Fetch Error:', error?.response);
//         reject(error?.response);
//       });
//   });
// };

interface ShipmentProductListResponse<T = unknown> {
  data: {
    data: {
      data: {
        products: T[];
        product_detail_columns?: ProductDetailColumns;
        page: number;
        page_size: number;
        products_count: number;
        total_count: number;
        page_count: number;
      };
      is_success: boolean;
      message?: string;
      status_code?: number;
    };
  };
}

export const fetchShipmentProducts = (
  token: string,
  model: ProductModelData,
  legType?: legTypes
): Promise<ShipmentProductListResponse> => {
  const url =
    legType === 'EL'
      ? `${APIPaths.executionLegProducts}`
      : `${APIPaths.shipmentProducts}`;
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost('Post', url, model, token, null, OperationType.Read)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.error('error?.response?.data?.error', error?.response);
        reject(error?.response);
      });
  });
};

const PRODUCT_LINES_PAGE_SIZE = 500;

/** Every line of one shipment, paged through `products/` because its `page_size: 0` means 50; the caller shows failures itself. Rows mirror `ShipmentProductListSerializer`. */
export async function fetchAllShipmentProductLines(
  shipmentId: string
): Promise<ShipmentProductList[]> {
  const lines: ShipmentProductList[] = [];
  for (let page = 1; ; page += 1) {
    const res = (await interceptor.axiosPost(
      'Post',
      APIPaths.shipmentProducts,
      { shipment_id: shipmentId, page, page_size: PRODUCT_LINES_PAGE_SIZE },
      undefined,
      null,
      OperationType.Read,
      'keep-auth'
    )) as ShipmentProductListResponse<ShipmentProductList>;
    const body = res.data.data;
    if (!body.is_success) {
      throw new Error(body.message || 'Products request failed');
    }
    lines.push(...body.data.products);
    if (lines.length >= body.data.total_count) return lines;
    if (body.data.products.length === 0) {
      throw new Error('Products list ended before its total count');
    }
  }
}

// export const fetchShipmentProductsDownload = (
//   token: string,
//   model: ProductModelData
// ): Promise<ShipmentProductListResponse> => {
//   return new Promise((resolve, reject) => {
//     interceptor
//       .axiosPost('Post', `${APIPaths.shipmentProducts}download/`, model, token)
//       .then((res) => {
//         resolve(res);
//       })
//       .catch((error) => {
//         console.error('error?.response?.data?.error', error?.response);
//         reject(error?.response);
//       });
//   });
// };

export const fetchShipmentProductsDownload = async (
  token: string,
  model: ProductModelData,
  legType?: legTypes
): Promise<AxiosResponse<Blob>> => {
  const requestUrl =
    legType === 'EL'
      ? `${baseUrl}${APIPaths.executionLegProducts}download/`
      : `${baseUrl}${APIPaths.shipmentProducts}download/`;

  const res = await axios({
    method: 'POST',
    url: requestUrl,
    data: model,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    responseType: 'blob',
  });

  return res;
};

export interface ShipmentLegListModel {
  shipment_id?: string;
  select_all?: boolean;
  exclude_shipment_ids?: string[];
  shipment_ids?: string[];
  filters?: Record<string, unknown>;
  leg_type: legTypes;
  search?: string;
  sort_key?: string;
  sort_order?: 'ascending' | 'descending';
  page?: number;
  page_size?: number;
  file_type?: 'csv' | 'xlsx' | 'pdf';
}

interface ShipmentLegListResponse {
  data?: {
    data?: {
      data?: {
        legs: ShipmentLegListItem[];
        page: number;
        page_size: number;
        legs_count: number;
        total_count: number;
        page_count: number;
        sort_key: string;
        sort_order: 'ascending' | 'descending';
        leg_type: legTypes;
      };
      is_success: boolean;
      message?: string;
      status_code?: number;
    };
  };
}

export const fetchShipmentLegs = (
  token: string,
  model: ShipmentLegListModel
): Promise<ShipmentLegListResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost(
        'Post',
        APIPaths.shipmentLegs,
        model,
        token,
        null,
        OperationType.Read
      )
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.error('error?.response?.data?.error', error?.response);
        reject(error?.response);
      });
  });
};

export const fetchShipmentLegsDownload = async (
  token: string,
  model: ShipmentLegListModel
): Promise<AxiosResponse<Blob>> => {
  const requestUrl = `${baseUrl}${APIPaths.shipmentLegs}download/`;

  const res = await axios({
    method: 'POST',
    url: requestUrl,
    data: model,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    responseType: 'blob',
  });

  return res;
};

// shipment table headers api
interface ShipmentHeaderItem {
  key: string;
  value: string;
  enable: boolean;
  group_name: string;
  required: boolean;
  is_custom: boolean;
}
interface ShipmentHeadersResponse {
  data?: {
    data?: {
      columns?: ShipmentHeaderItem[];
    };
  };
}
export const GetShipmentHeaders = (
  token: string,
  query: string
): Promise<ShipmentHeadersResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', `${APIPaths.shipment_headers}?${query}`, token)
      .then((res) => {
        resolve(res.data || null);
      })
      .catch((error) => {
        console.error('Error fetching shipments:', error?.response);
        reject(error?.response);
      });
  });
};

export const updateShipmentColumns = (
  token: string,
  model: unknown
): Promise<unknown> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPut('Put', `${APIPaths.shipment_headers}`, model, token)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.error('updateShipmentColumns error:', error?.response);
        reject(error?.response);
      });
  });
};

export interface UpdateDeliveryAddressTrackingLinkModel {
  token: string;
  new_address_line_1: string;
  new_address_line_2?: string;
  new_city: string;
  new_state: string;
  new_postal_code: string;
  new_country: string;
  change_scope: 'PERMANENT' | 'TEMPORARY';
  temporary_start_date?: string;
  temporary_end_date?: string;
  issue_type: string;
}

export const updateDeliveryAddressTrackingLink = (
  model: UpdateDeliveryAddressTrackingLinkModel
): Promise<ApiResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost('Post', APIPaths.updateDeliveryAddressTL, model)
      .then((res) => resolve(res))
      .catch((error) => {
        console.error('Share shipment issues Error:', error?.response);
        reject(error?.response);
      });
  });
};

// Every route deviation call is silent: the modal turns a refusal into its
// own inline message, so the global toast must not fire as well.
export const GetRouteDeviationCandidates = (
  token: string,
  shipmentId: string,
  milesOff: number,
  signal?: AbortSignal
): Promise<RouteDeviationCandidates> => {
  const url = `${APIPaths.routeDeviationCandidates(shipmentId)}?miles_off=${milesOff}`;

  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', url, token, undefined, signal, true)
      .then((res) =>
        resolve((res as ApiResponse<RouteDeviationCandidates>).data.data.data)
      )
      .catch((error) => reject(error?.response));
  });
};

export const GetRouteDeviation = (
  token: string,
  shipmentId: string,
  signal?: AbortSignal
): Promise<RouteDeviationLookup> => {
  const url = APIPaths.routeDeviationSetting(shipmentId);

  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', url, token, undefined, signal, true)
      .then((res) => {
        const body = (res as ApiResponse<RouteDeviationSetting | null>).data
          .data as {
          data: RouteDeviationSetting | null;
          reason?: 'lane_changed';
        };
        resolve({ setting: body.data ?? null, reason: body.reason });
      })
      .catch((error) => reject(error?.response));
  });
};

export const SaveRouteDeviation = (
  token: string,
  shipmentId: string,
  model: SaveRouteDeviationPayload
): Promise<RouteDeviationSetting> => {
  const url = APIPaths.routeDeviationSetting(shipmentId);

  return new Promise((resolve, reject) => {
    interceptor
      .axiosPut(
        'Put',
        url,
        model,
        token,
        undefined,
        undefined,
        OperationType.Write,
        true
      )
      .then((res) =>
        resolve((res as ApiResponse<RouteDeviationSetting>).data.data.data)
      )
      .catch((error) => reject(error?.response));
  });
};

export const DeleteRouteDeviation = (
  token: string,
  shipmentId: string
): Promise<void> => {
  const url = APIPaths.routeDeviationSetting(shipmentId);

  return new Promise((resolve, reject) => {
    interceptor
      .axiosDelete(
        'Delete',
        url,
        null,
        token,
        undefined,
        OperationType.Write,
        true
      )
      .then(() => resolve())
      .catch((error) => reject(error?.response));
  });
};

/** The company's setting for the alert a simulator scenario template raises. */
export interface SimulatorAlertThreshold {
  alert_type: string;
  phase: string;
  /** False when no selected team has the alert on. */
  enabled: boolean;
  /** Minutes, percent or G, whichever the alert uses; null when it has no number. */
  threshold: number | null;
}

/** Alert settings by scenario type for the shipment's teams (the default team when none). */
export const GetSimulatorAlertThresholds = (
  token: string,
  teamIds: string[]
): Promise<
  AxiosResponse<{ templates: Record<string, SimulatorAlertThreshold> }>
> => {
  const params = new URLSearchParams();
  teamIds.forEach((id) => params.append('team_ids', id));
  const query = params.toString();
  const url = query
    ? `${APIPaths.simulatorAlertThresholds}?${query}`
    : APIPaths.simulatorAlertThresholds;
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', url, token)
      .then((res) => resolve(res))
      .catch((error) => reject(error?.response));
  });
};
